import { useMemo } from 'react';
import { useGiftCardBalance } from '@olo-web/domain';
import { useOrder } from '@olo-web/domain/orders/queries/useOrder';
import { formatPrice } from '@olo-web/domain/orders/functions/formatPrice';
import { sumPayments } from '@olo-web/domain/orders/functions/sumPayments';
import { usePaymentMethodValues } from '@olo-web/domain/payments/hooks';
import { useIsSplitByAmount } from '@olo-web/utils/common/hooks/useIsSplitByAmount';
import { useTipAmount } from './useTipAmount';
import { useCustomPaymentAmount } from './useCustomPaymentAmount';

export const useCheckoutOrderTotal = (): string => {
  const { data: order } = useOrder();
  const { isCash } = usePaymentMethodValues();
  const tipAmount = useTipAmount();
  const { data: giftCard } = useGiftCardBalance();
  const isSplitByAmount = useIsSplitByAmount();
  const { total: amount, itemTotal } = useCustomPaymentAmount();

  return useMemo(() => {
    let total = amount ?? +order?.displayTotalAmount ?? 0;

    if (+tipAmount > 0) {
      total = total + +tipAmount;
    }
    if (order?.payments.length && !isSplitByAmount && itemTotal === null) {
      total -= sumPayments(order?.payments);
    }
    if (total <= +giftCard?.remainingBalanceAmount && !isCash) {
      total = 0;
    } else if (giftCard?.remainingBalanceAmount && !isCash) {
      total = total - +giftCard?.remainingBalanceAmount;
      if (total < 0) total = 0;
    }

    return formatPrice(total);
  }, [
    amount,
    order?.displayTotalAmount,
    order?.payments,
    tipAmount,
    isSplitByAmount,
    itemTotal,
    giftCard?.remainingBalanceAmount,
    isCash,
  ]);
};
